<template>
  <section id="batch-price-table">
    <b-table
      :data="items"
      :mobile-cards="false"
      narrowed
      sticky-header
      :row-class="rowClass"
      :height="height"
    >
      <b-table-column label="" width="40" centered v-slot="props">
        <b-tooltip v-show="props.row.error" position="is-right">
          <b-icon icon="information" type="is-danger"></b-icon>
          <template v-slot:content>
            {{ props.row.error }}
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.group_code')"
        width="100"
        centered
        sortable
        v-slot="props"
      >
        <b-tooltip type="is-light" position="is-right">
          <p>{{ props.row.code }}</p>
          <template v-slot:content>
            <p v-html="props.row.name"></p>
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="departure_date"
        :label="$t('attributes.departure_date')"
        width="140"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDate(props.row.departure_date) }}
      </b-table-column>

      <b-table-column
        :label="$t('attributes.deposit')"
        width="240"
        centered
        v-slot="props"
      >
        <b-input
          :ref="`deposit-input-${props.row.code}`"
          v-model.number="props.row.deposit"
          name="deposit"
          type="number"
          min="0"
          :use-html5-validation="false"
          :placeholder="$t('message.please_input') + $t('attributes.deposit')"
          @input="depositModified(props.row)"
          @keypress.enter.exact.native.prevent="
            finish(props.row, props.index + 1, 'deposit')
          "
          @keypress.enter.shift.exact.native.prevent="
            finish(props.row, props.index - 1, 'deposit')
          "
        ></b-input>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.hide_price')"
        width="100"
        centered
        v-slot="props"
      >
        <b-field>
          <b-radio-button
            v-model="props.row.hide_price"
            :name="`hide_price-${props.row.code}`"
            native-value="N"
            type="is-success is-light is-outlined"
            @input="modified(props.row)"
          >
            <span>{{ $t('attributes.display') }}</span>
          </b-radio-button>
          <b-radio-button
            v-model="props.row.hide_price"
            :name="`hide_price-${props.row.code}`"
            native-value="Y"
            type="is-danger is-light is-outlined"
            @input="modified(props.row)"
          >
            <span>{{ $t('attributes.hide') }}</span>
          </b-radio-button>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.show_up')"
        width="150"
        centered
        v-slot="props"
      >
        <b-field>
          <b-radio-button
            v-model="props.row.show_up"
            :name="`show_up-${props.row.code}`"
            native-value="Y"
            type="is-success is-light is-outlined"
            @input="modified(props.row)"
          >
            <span>{{ $t('attributes.yes') }}</span>
          </b-radio-button>
          <b-radio-button
            v-model="props.row.show_up"
            :name="`show_up-${props.row.code}`"
            native-value="N"
            type="is-danger is-light is-outlined"
            @input="modified(props.row)"
          >
            <span>{{ $t('attributes.no') }}</span>
          </b-radio-button>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.tip_fee')"
        width="200"
        centered
        v-slot="props"
      >
        <b-field>
          <b-select
            :ref="`tip-flag-input-${props.row.code}`"
            v-model="props.row.tip_flag"
            name="tip_flag"
            @input="modified(props.row)"
          >
            <option value="Y">{{ $t('attributes.included') }}</option>
            <option value="N">{{ $t('attributes.excluded') }}</option>
          </b-select>
          <b-input
            :ref="`tip-fee-input-${props.row.code}`"
            v-model.number="props.row.tip_fee"
            name="tip_fee"
            type="number"
            min="0"
            :use-html5-validation="false"
            :placeholder="$t('message.please_input') + $t('attributes.tip_fee')"
            expanded
            @input="tipFeeModified(props.row)"
            @keypress.enter.exact.native.prevent="
              finish(props.row, props.index + 1, 'tip-fee')
            "
            @keypress.enter.shift.exact.native.prevent="
              finish(props.row, props.index - 1, 'tip-fee')
            "
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.tax_fee')"
        width="200"
        centered
        v-slot="props"
      >
        <b-field>
          <b-select
            :ref="`insurance-flag-input-${props.row.code}`"
            v-model="props.row.insurance_flag"
            name="insurance_flag"
            @input="modified(props.row)"
          >
            <option value="Y">{{ $t('attributes.included') }}</option>
            <option value="N">{{ $t('attributes.excluded') }}</option>
          </b-select>
          <b-input
            :ref="`insurance-fee-input-${props.row.code}`"
            v-model.number="props.row.insurance_fee"
            name="insurance_fee"
            type="number"
            min="0"
            :use-html5-validation="false"
            :placeholder="$t('message.please_input') + $t('attributes.tax_fee')"
            expanded
            @input="taxFeeModified(props.row)"
            @keypress.enter.exact.native.prevent="
              finish(props.row, props.index + 1, 'insurance-fee')
            "
            @keypress.enter.shift.exact.native.prevent="
              finish(props.row, props.index - 1, 'insurance-fee')
            "
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.visa_fee')"
        width="220"
        centered
        v-slot="props"
      >
        <b-field>
          <b-select
            :ref="`visa-flag-input-${props.row.code}`"
            v-model="props.row.visa_flag"
            name="visa_flag"
            @input="modified(props.row)"
          >
            <option value="Y">{{ $t('attributes.included') }}</option>
            <option value="N">{{ $t('attributes.excluded') }}</option>
            <option value="X">{{ $t('attributes.visa_free') }}</option>
          </b-select>
          <b-input
            :ref="`visa-fee-input-${props.row.code}`"
            v-model.number="props.row.visa_fee"
            name="visa_fee"
            type="number"
            min="0"
            :use-html5-validation="false"
            :placeholder="
              $t('message.please_input') + $t('attributes.visa_fee')
            "
            expanded
            @input="visaFeeModified(props.row)"
            @keypress.enter.exact.native.prevent="
              finish(props.row, props.index + 1, 'visa-fee')
            "
            @keypress.enter.shift.exact.native.prevent="
              finish(props.row, props.index - 1, 'visa-fee')
            "
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.discount_limit')"
        width="200"
        centered
        v-slot="props"
      >
        <b-input
          :ref="`discount-limit-input-${props.row.code}`"
          v-model.number="props.row.discount_limit"
          name="discount_limit"
          type="number"
          min="0"
          :use-html5-validation="false"
          :placeholder="
            $t('message.please_input') + $t('attributes.discount_limit')
          "
          @input="discountModified(props.row)"
          @keypress.enter.exact.native.prevent="
            finish(props.row, props.index + 1, 'discount-limit')
          "
          @keypress.enter.shift.exact.native.prevent="
            finish(props.row, props.index - 1, 'discount-limit')
          "
        ></b-input>
      </b-table-column>

      <b-table-column
        width="50"
        :label="$t('attributes.function')"
        centered
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-tooltip :label="$t('attributes.restore')">
            <b-button
              type="is-info"
              icon-left="restore"
              outlined
              @click.prevent="restore(props.row)"
            >
            </b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section" style="height: 100%">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>
  </section>
</template>

<script>
export default {
  name: 'BatchPriceTable',

  props: {
    groups: {
      Type: Array,
      required: true
    },
    height: Number,
    loading: Boolean
  },
  data() {
    return {
      items: this.extractGroups(this.groups)
    }
  },
  watch: {
    groups(list) {
      this.items = this.extractGroups(list)
    }
  },
  methods: {
    extractGroups(groups) {
      return groups.map(group => {
        return {
          code: group.code,
          departure_date: group.departure_date,
          name: group.name,
          deposit: group.deposit || 0,
          tip_flag: group.tip_flag ? group.tip_flag.trim() : 'N',
          tip_fee: group.tip_fee || 0,
          insurance_flag: group.insurance_flag
            ? group.insurance_flag.trim()
            : 'N',
          insurance_fee: group.insurance_fee || 0,
          visa_flag: group.visa_flag ? group.visa_flag.trim() : 'N',
          visa_fee: group.visa_fee || 0,
          hide_price: group.hide_price ? group.hide_price.trim() : 'N',
          show_up: group.show_up ? group.show_up.trim() : 'N',
          discount_limit: group.discount_limit,

          editing: false,
          dirty: false,
          error: null,
          origin: {
            ...group,
            deposit: group.deposit || 0,
            tip_flag: group.tip_flag ? group.tip_flag.trim() : 'N',
            tip_fee: group.tip_fee || 0,
            insurance_flag: group.insurance_flag
              ? group.insurance_flag.trim()
              : 'N',
            insurance_fee: group.insurance_fee || 0,
            visa_flag: group.visa_flag ? group.visa_flag.trim() : 'N',
            visa_fee: group.visa_fee || 0,
            hide_price: group.hide_price ? group.hide_price.trim() : 'N',
            show_up: group.show_up ? group.show_up.trim() : 'N',
            discount_limit: group.discount_limit
          }
        }
      })
    },
    depositModified(group) {
      const deposit = parseInt(group.deposit)

      if (isNaN(deposit)) {
        group.deposit = 0
      }

      this.modified(group)
    },
    tipFeeModified(group) {
      const fee = parseInt(group.tip_fee)

      if (isNaN(fee)) {
        group.tip_fee = 0
      }

      this.modified(group)
    },
    taxFeeModified(group) {
      const fee = parseInt(group.insurance_fee)

      if (isNaN(fee)) {
        group.insurance_fee = 0
      }

      this.modified(group)
    },
    visaFeeModified(group) {
      const fee = parseInt(group.visa_fee)

      if (isNaN(fee)) {
        group.visa_fee = 0
      }

      this.modified(group)
    },
    discountModified(group) {
      const discount = parseInt(group.discount_limit)

      if (isNaN(discount)) {
        group.discount_limit = 0
      }

      this.modified(group)
    },
    finish(group, next, field) {
      this.modified(group)

      let index = (next + this.items.length) % this.items.length
      if (index >= 0 && index < this.items.length) {
        const nextGroup = this.items[index]

        setTimeout(() => {
          const name = `${field}-input-${nextGroup.code}`
          const input = this.$refs[name].getElement()
          if (input) {
            input.focus()
          }
        }, 0)
      }
    },
    modified(group) {
      group.dirty =
        group.deposit !== group.origin.deposit ||
        group.hide_price !== group.origin.hide_price ||
        group.show_up !== group.origin.show_up ||
        group.discount_limit !== group.origin.discount_limit ||
        group.tip_flag !== group.origin.tip_flag ||
        group.tip_fee !== group.origin.tip_fee ||
        group.insurance_flag !== group.origin.insurance_flag ||
        group.insurance_fee !== group.origin.insurance_fee ||
        group.visa_flag !== group.origin.visa_flag ||
        group.visa_fee !== group.origin.visa_fee

      this.validate(group)
    },
    restore(group) {
      group.deposit = group.origin.deposit
      group.tip_flag = group.origin.tip_flag
      group.tip_fee = group.origin.tip_fee
      group.insurance_flag = group.origin.insurance_flag
      group.insurance_fee = group.origin.insurance_fee
      group.visa_flag = group.origin.visa_flag
      group.visa_fee = group.origin.visa_fee
      group.hide_price = group.origin.hide_price
      group.show_up = group.origin.show_up
      group.discount_limit = group.origin.discount_limit

      group.dirty = false
      group.error = false
    },
    isDirty() {
      return this.items.filter(x => x.dirty).length > 0
    },
    isEditing() {
      return this.items.filter(x => x.editing).length > 0
    },
    isValidate() {
      for (const group of this.items) {
        if (!this.validate(group)) {
          return false
        }
      }

      return true
    },
    validate(group) {
      if (group.deposit < 0) {
        group.error = '訂金不可小於0'
        return false
      }
      if (group.tip_fee < 0) {
        group.error = '小費不可小於0'
        return false
      }
      if (group.insurance_fee < 0) {
        group.error = '稅金不可小於0'
        return false
      }
      if (group.visa_fee < 0) {
        group.error = '簽證費不可小於0'
        return false
      }
      if (group.discount_limit < 0) {
        group.error = '團費折抵上限不可小於0'
        return false
      }

      group.error = null
      return true
    },
    getData() {
      return this.items
        .filter(group => group.dirty)
        .map(group => ({
          code: group.code,
          deposit: group.deposit,
          hide_price: group.hide_price,
          show_up: group.show_up,
          discount_limit: group.discount_limit,
          tip_flag: group.tip_flag,
          tip_fee: group.tip_fee,
          insurance_flag: group.insurance_flag,
          insurance_fee: group.insurance_fee,
          visa_flag: group.visa_flag,
          visa_fee: group.visa_fee
        }))
    },
    rowClass(group) {
      if (group.error) {
        return 'has-background-danger-light'
      } else if (group.dirty) {
        return 'has-background-warning'
      } else {
        return null
      }
    }
  }
}
</script>

<style lang="scss">
#batch-price-table {
  .b-table .table {
    min-width: 1480px;
  }
}
</style>
